import { ChangeDetectionStrategy, Component } from '@angular/core';
import { TypedBaseListComponent, SharedModule, getServerLocation } from '@vendure/admin-ui/core';
import { GetFeedbacksDocument, FeedbackSentiment, FeedbackType } from '../../generated-types';
import { ChangeDetectorRef } from '@angular/core';

@Component({
    selector: 'feedback-list',
    templateUrl: './feedback-list.component.html',
    styleUrls: ['./feedback-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [SharedModule],
})
export class FeedbackListComponent extends TypedBaseListComponent<typeof GetFeedbacksDocument, 'feedbacks'> {
    isLoading;
    readonly filters = this.createFilterCollection()
        .addIdFilter()
        .addDateFilters()
        .addFilter({
            name: 'subject',
            type: { kind: 'text' },
            label: 'Subject',
            filterField: 'subject',
        })
        .addFilter({
            name: 'message',
            type: { kind: 'text' },
            label: 'Message',
            filterField: 'message',
        })
        .addFilter({
            name: 'type',
            type: {
                kind: 'select',
                options: Object.values(FeedbackType).map(type => ({
                    label: type,
                    value: type,
                })),
            },
            label: 'Type',
            filterField: 'type',
        })
        .addFilter({
            name: 'tag',
            type: { kind: 'text' },
            label: 'Tag',
            filterField: 'tag',
        })
        .addFilter({
            name: 'sentiment',
            type: {
                kind: 'select',
                options: Object.values(FeedbackSentiment).map(sentiment => ({
                    label: sentiment,
                    value: sentiment,
                })),
            },
            label: 'Sentiment',
            filterField: 'sentiment',
        })
        .addFilter({
            name: 'orderCode',
            type: { kind: 'text' },
            label: 'Order Code',
            filterField: 'orderCode',
        })
        .addFilter({
            name: 'storeName',
            type: { kind: 'text' },
            label: 'Store Name',
            filterField: 'storeName',
        })
        .addFilter({
            name: 'customerFirstName',
            type: { kind: 'text' },
            label: 'Customer First Name',
            filterField: 'customerFirstName',
        })
        .addFilter({
            name: 'customerLastName',
            type: { kind: 'text' },
            label: 'Customer Last Name',
            filterField: 'customerLastName',
        })
        .addFilter({
            name: 'deviceType',
            type: { kind: 'text' },
            label: 'Device Type',
            filterField: 'deviceType',
        })
        .connectToRoute(this.route);

    readonly sorts = this.createSortCollection()
        .defaultSort('createdAt', 'DESC')
        .addSort({ name: 'id' })
        .addSort({ name: 'createdAt' })
        .addSort({ name: 'updatedAt' })
        .addSort({ name: 'subject' })
        .addSort({ name: 'message' })
        .addSort({ name: 'type' })
        .addSort({ name: 'tag' })
        .addSort({ name: 'sentiment' })
        .addSort({ name: 'orderCode' })
        .addSort({ name: 'storeName' })
        .addSort({ name: 'customerFirstName' })
        .addSort({ name: 'customerLastName' })
        .addSort({ name: 'deviceType' })
        .connectToRoute(this.route);

        constructor(private cdRef: ChangeDetectorRef) {
            super();
            super.configure({
                document: GetFeedbacksDocument,
                getItems: data => data.feedbacks,
                setVariables: (skip, take) => ({
                    options: {
                        skip,
                        take,
                        filter: this.filters.createFilterInput(),
                        sort: this.sorts.createSortInput(),
                    },
                }),
                refreshListOnChanges: [this.filters.valueChanges, this.sorts.valueChanges],
            });
        }

    //Adding the spinner
    async downloadFeedbacks() {
        this.isLoading = true;
    
        try {
            const res = await fetch(`${getServerLocation()}/export-feedbacks/export`);
            if (!res.ok) {
                const json = await res.json();
                throw new Error(json?.message);
            }
    
            const header = res.headers.get('Content-Disposition');
            let effectiveHeader: string;
            if (header === null) {
                effectiveHeader = `inline; filename=feedback-export.csv`;
            } else {
                effectiveHeader = header;
            }
    
            const parts = effectiveHeader.split(';');
            const filename = parts[1].split('=')[1];
            const blob = await res.blob();
            await this.downloadBlob(blob, filename);
        } catch (err: any) {
            console.error(err);
        } finally {
            this.isLoading = false;
            this.cdRef.detectChanges();
        }
    }
    
    
    async downloadBlob(blob: Blob, fileName: string) {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.setAttribute('hidden', 'true');
        a.href = blobUrl;
        a.download = fileName;
        a.setAttribute('target', '_blank');
        a.click();
        document.body.removeChild(a);
    }
}
